import React from 'react';
import {
  ReviewPolicyNodeData,
  ReviewSourceNodeData,
  ReviewVerdictNodeData,
  ReviewerCoreNodeData,
  ReviewerNode,
  ReviewerNodeType,
  ReviewerStatus,
} from './graphTypes';
import ReviewerNodeDetail from './ReviewerNodeDetail';
import { StatusGlyph, outcomeBadgeClass, traceOutcomeBadgeClass } from './nodes/reviewerNodeStyles';
import { detailHeading, detailMuted } from './reviewerDetailStyles';

/** One row's line, derived from whatever kind of node it is. */
interface StageLine {
  label: string;
  status?: ReviewerStatus;
  detail?: string;
  /** `fired` / `skipped` (a policy) — the run's own record. */
  outcome?: string;
  /** One `[tier, outcome]` per judge call the run made (the judge row). */
  tierOutcomes?: [string, string][];
}

const lineOf = (node: ReviewerNode): StageLine => {
  const data = node.data;
  if (data.kind === 'review-source') {
    const source = (data as ReviewSourceNodeData).source;
    return { label: source.label, status: data.status, detail: source.detail };
  }
  if (data.kind === 'reviewer-core') {
    const core = data as ReviewerCoreNodeData;
    return {
      label: 'Reviewer',
      detail: core.checkpoint.reviewed
        ? `mode ${core.mode || 'unset'} · ${core.checkpoint.tiers.length} tiers`
        : core.checkpoint.reason || 'the reviewer does not run here',
      tierOutcomes: Object.entries(core.tierOutcomes),
    };
  }
  if (data.kind === 'review-policy') {
    const policy = (data as ReviewPolicyNodeData).policy;
    return {
      label: policy.label,
      status: data.status,
      detail: `on ${policy.verdict}`,
      outcome: (data as ReviewPolicyNodeData).outcome,
    };
  }
  const verdict = data as ReviewVerdictNodeData;
  return { label: 'The ruling', detail: verdict.verdicts.join(' · ') };
};

const StageRow: React.FC<{
  node: ReviewerNode;
  selected: boolean;
  onSelect: (nodeId: string) => void;
}> = ({ node, selected, onSelect }) => {
  const line = lineOf(node);
  return (
    <div>
      <button
        type="button"
        data-testid={`reviewer-stage-row-${node.id}`}
        data-selected={selected ? 'true' : undefined}
        aria-expanded={selected}
        onClick={() => onSelect(node.id)}
        className={`flex w-full items-center gap-1.5 border px-2 py-1.5 text-left transition-colors ${
          selected
            ? 'border-[var(--accent-strong)] bg-[var(--surface-2)]'
            : 'border-[var(--border-strong)] bg-[var(--surface-1)]'
        }`}
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate font-mono text-[11px] text-[var(--app-text)]">
            {line.label}
          </span>
          {line.detail && <span className={`block truncate ${detailMuted}`}>{line.detail}</span>}
        </span>
        {(line.tierOutcomes || []).map(([tier, outcome]) => (
          <span
            key={tier}
            className={traceOutcomeBadgeClass(outcome)}
            data-testid={`reviewer-stage-tier-${tier}`}
          >
            {`${tier} ${outcome}`}
          </span>
        ))}
        {line.outcome && (
          <span className={outcomeBadgeClass} data-testid="reviewer-stage-outcome">
            {line.outcome}
          </span>
        )}
        {line.status && <StatusGlyph status={line.status} label={line.label} />}
      </button>
      {selected && (
        <div className="mt-1">
          <ReviewerNodeDetail node={node} />
        </div>
      )}
    </div>
  );
};

const LANES: { id: string; heading: string; type: ReviewerNodeType }[] = [
  { id: 'sources', heading: 'What it reads', type: 'review-source' },
  { id: 'judge', heading: 'The judge', type: 'reviewer-core' },
  { id: 'policies', heading: 'What it may do', type: 'review-policy' },
  { id: 'verdict', heading: 'The ruling', type: 'review-verdict' },
];

export interface ReviewerStageListProps {
  /** The very nodes the canvas would draw — one derivation, two arrangements. */
  nodes: ReviewerNode[];
  selectedId?: string | null;
  onSelect: (nodeId: string) => void;
}

/**
 * The phone arrangement of the same graph: a node-graph needs pan, zoom and
 * two dimensions, none of which a phone has, so below `md` the picture becomes
 * stacked lanes in reading order — what the judge reads, the judge, what it may
 * then do, and what it can rule.
 *
 * Rows are full-width and truncate, so the list never scrolls sideways, and a
 * tap opens exactly the block the desktop column shows for that node.
 */
const ReviewerStageList: React.FC<ReviewerStageListProps> = ({ nodes, selectedId, onSelect }) => (
  <div className="w-full space-y-3" data-testid="reviewer-stage-list">
    {LANES.map((lane) => {
      const laneNodes = nodes.filter((node) => node.type === lane.type);
      if (laneNodes.length === 0) return null;
      return (
        <section key={lane.id} className="space-y-1" data-testid={`reviewer-stage-lane-${lane.id}`}>
          <h4 className={detailHeading}>{lane.heading}</h4>
          {laneNodes.map((node) => (
            <StageRow
              key={node.id}
              node={node}
              selected={node.id === selectedId}
              onSelect={onSelect}
            />
          ))}
        </section>
      );
    })}
  </div>
);

export default ReviewerStageList;
